import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type FC, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { translations } from '../translations';

interface TranslationContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  /** String arrays from translations (e.g. bullet lists). Do not use `t()` for array leaves — it would return the raw key. */
  tArray: (key: string) => string[];
  currentLanguage: string;
  setLanguage: (lang: string) => void;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { currentLanguage, setCurrentLanguage } = useAuth();
  const cacheRef = useRef(new Map<string, string>());

  useEffect(() => {
    cacheRef.current.clear();
  }, [currentLanguage]);

  const tArray = useCallback((key: string): string[] => {
    const keys = key.split('.');
    let value: unknown = translations[currentLanguage as keyof typeof translations];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in (value as object)) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return [];
      }
    }

    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item) => (typeof item === 'string' ? item : String(item)));
  }, [currentLanguage]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const paramsKey =
      params && Object.keys(params).length > 0 ? JSON.stringify(params) : '';
    const cacheKey = `${currentLanguage}|${key}|${paramsKey}`;
    const hit = cacheRef.current.get(cacheKey);
    if (hit !== undefined) {
      return hit;
    }

    const keys = key.split('.');
    let value: any = translations[currentLanguage as keyof typeof translations];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        cacheRef.current.set(cacheKey, key);
        return key;
      }
    }

    if (typeof value === 'string' && params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
      });
    }

    const resolved = value || key;
    if (Array.isArray(resolved)) {
      return key;
    }

    cacheRef.current.set(cacheKey, typeof resolved === 'string' ? resolved : String(resolved));
    return typeof resolved === 'string' ? resolved : String(resolved);
  }, [currentLanguage]);

  const handleSetLanguage = useCallback(
    (lang: string) => {
      setCurrentLanguage(lang);
      document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    },
    [setCurrentLanguage],
  );

  const contextValue = useMemo(
    () => ({
      t,
      tArray,
      currentLanguage,
      setLanguage: handleSetLanguage,
    }),
    [t, tArray, currentLanguage, handleSetLanguage],
  );

  return <TranslationContext.Provider value={contextValue}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
